import {
  alpha,
  Box,
  Card,
  Divider,
  Grid,
  Paper,
  Typography,
  IconButton,
  Chip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import {
  DataLayout,
  MetaDrivenTable,
  StatusChip,
} from "../../../shared/components";
import { PerformanceComparison } from "./PerformanceComparison";
import { KpiCard } from "../../programmeDashboard/components/KpiCard";

import { useFetchState } from "../hooks/useFetchState";
import { useFetchContractor } from "../hooks/useFetchContractor";
import { useFetchContractorRank } from "../hooks/useFetchContractorRank";
import { useFetchGraph } from "../hooks/useFetchGraph";

import { getIconColor } from "../utils/getIconColor";
import { getIcon } from "../utils/getIcon";

export const ContractorComparison = () => {

  const theme = useTheme();

  const { isLoading: isLoadingState, isError: isErrorState, error: errorState, data: stateData } = useFetchState();
  const { isLoading: isLoadingContractor, isError: isErrorContractor, error: errorContractor, data: contractorData } = useFetchContractor();
  const { isLoading: isLoadingRank, isError: isErrorRank, error: errorRank, headers: rankHeaders, data: rankData } = useFetchContractorRank();
  const { isLoading: isLoadingGraph, isError: isErrorGraph, error: errorGraph, data: graphData } = useFetchGraph();

  if (isLoadingState || isLoadingContractor || isLoadingRank || isLoadingGraph) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    )
  };

  if (isErrorState || isErrorContractor || isErrorRank || isErrorGraph) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <Typography variant="h6" color="error">
          {errorState?.message || errorContractor?.message || errorRank?.message || errorGraph?.message || "Error loading data."}
        </Typography>
      </Box>
    )
  };

  return (
    <Grid container spacing={2} p={2}>

      <Grid size={12} container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Total Contractors"
            value={stateData?.total_contractor ?? "-"}
            icon={getIcon("contractors")}
            iconColor={getIconColor("contractors", theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Active Projects"
            value={stateData?.active_project_count ?? "-"}
            icon={getIcon("projects")}
            iconColor={getIconColor("projects", theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Total Activities"
            value={stateData?.activity_count ?? "-"}
            icon={getIcon("activities")}
            iconColor={getIconColor("activities", theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Avg. On-Time Delivery %"
            value={
              stateData?.avg_on_time_delivery_percent !== undefined
                ? `${stateData.avg_on_time_delivery_percent}%`
                : "-"
            }
            icon={getIcon("delivery")}
            iconColor={getIconColor("delivery", theme)}
          />
        </Grid>
      </Grid>

      <Grid size={12}>
        <Grid container spacing={2}>
          {contractorData?.map((detail, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
              <Card
                variant="outlined"
                sx={{
                  width: "100%",
                  p: 2,

                  height: "100%",
                }}
              >
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  mb={2}
                >
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Typography variant="subtitle2" color="text.primary">
                      {detail.contractor_name}
                    </Typography>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor:
                          detail.overall_rag === "amber"
                            ? "warning.main"
                            : detail.overall_rag === "red"
                              ? "error.main"
                              : "success.main",
                      }}
                    />
                    {detail.metrics.on_time_delivery.rag === "red" ? (
                      <TrendingDownIcon
                        sx={{ height: 20, width: 20, color: "error.main" }}
                      />
                    ) : detail.metrics.on_time_delivery.rag === "green" ? (
                      <TrendingUpIcon
                        sx={{ height: 20, width: 20, color: "success.main" }}
                      />
                    ) : null}
                  </Box>
                  <IconButton size="small" color="primary">
                    <NorthEastIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box display={"flex"} gap={1} mb={2} alignItems={"center"}>
                  <Typography variant="body2" color="text.secondary">
                    Projects {detail.projects_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activities {detail.activities_count}
                  </Typography>
                </Box>
                <Grid container spacing={1} mb={2}>
                  <Grid size={6}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"start"}
                      px={1.5}
                      py={0.5}
                      bgcolor="action.hover"
                      borderRadius={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        On-Time Delivery
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                      >
                        {detail.metrics.on_time_delivery.value || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"start"}
                      px={1.5}
                      py={0.5}
                      bgcolor="action.hover"
                      borderRadius={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Responsiveness
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                      >
                        {detail.metrics.responsiveness.value || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"start"}
                      px={1.5}
                      py={0.5}
                      bgcolor="action.hover"
                      borderRadius={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Quality Score
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                      >
                        {detail.metrics.quality_score.value || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"start"}
                      px={1.5}
                      py={0.5}
                      bgcolor="action.hover"
                      borderRadius={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Cost Variance
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                      >
                        {detail.metrics.cost_variance.value || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body2" color="text.secondary">
                    Overdue Activities
                  </Typography>
                  <Chip
                    label={detail.overdue_activities}
                    color="error"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid size={12}>
        <DataLayout title="Performance Comparison" sx={{ height: "100%" }}>
          <PerformanceComparison data={graphData} />
        </DataLayout>
      </Grid>

      <Grid size={12}>
        <DataLayout title="Contractor Rankings">
          <MetaDrivenTable
            headers={rankHeaders}
            data={rankData || []}
            dataMetrics={{
              overall_rag: {
                type: "component",
                element: (row: { overall_rag: string }) => (
                  <StatusChip
                    label={
                      row.overall_rag.charAt(0).toUpperCase() + row.overall_rag.slice(1)
                    }
                    color={
                      row.overall_rag === "green"
                        ? "green"
                        : row.overall_rag === "amber"
                          ? "amber"
                          : "red"
                    }
                    variant="filled"
                  />
                )
              }
            }}
            sortDisabled
          />
        </DataLayout>
      </Grid>

      <Grid size={12}>
        <DataLayout title="Key Issues Requiring Attention">
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {(rankData?.filter(item => item.overall_rag === "red" || item.overall_rag === "amber") ?? []).length > 0 ? (
              <Box>
                {rankData
                  ?.filter(item => item.overall_rag === "red" || item.overall_rag === "amber")
                  .map((rankDataItem, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <StatusChip
                          label={rankDataItem.overall_rag === "red" ? "Critical" : "Warning"}
                          color={rankDataItem.overall_rag === "red" ? "red" : "amber"}
                          variant="filled"
                          size="small"
                        />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 150, flexShrink: 0 }}>
                          {rankDataItem.contractor_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{rankDataItem.overdue_activities || 0}</strong> overdue
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{rankDataItem.on_time_delivery || "N/A"}</strong> delivery
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{rankDataItem.cost_variance || "N/A"}</strong> cost var.
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Requires review
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={100}>
                <Typography variant="body2" color="text.secondary">No critical issues found</Typography>
              </Box>
            )}
          </Box>
        </DataLayout>
      </Grid>

    </Grid>
  );
};
